import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { gql } from "@apollo/client";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ReactLenis, { LenisRef } from "lenis/react";
import { SEO, SoundPopup } from "@components";
import { DigitalExhibitQuery } from "@graphqlTypes";
import { FaustTemplate } from "@faustwp/core";
import { DigitalExhibitDetailFragment } from "@/fragments/DigitalExhibit";
import { getBasicQueryVars, getPageTitle } from "@/core/utils";
import { getReduceMotion } from "@/utils/reduceMotion";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { selectValue } from "@/components/DigitalExhibit/utils";
import { resolveDxFont, resolveDxCase } from "@/lib/dx-fonts";
import DigitalExhibitTheme from "@/components/DigitalExhibit/DigitalExhibitTheme/DigitalExhibitTheme";
import DigitalExhibitHeroIntro from "@/components/DigitalExhibit/DigitalExhibitHeroIntro/DigitalExhibitHeroIntro";
import DigitalExhibitContentMap from "@/components/DigitalExhibit/DigitalExhibitContentMap";
import DigitalExhibitPageEnder from "@/components/DigitalExhibit/DigitalExhibitPageEnder/DigitalExhibitPageEnder";

const DigitalExhibit: FaustTemplate<DigitalExhibitQuery> = ({ data, loading }: FaustPageProps<DigitalExhibitQuery>) => {
  const { setHighlightColor } = usePageHighlight();
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const { siteTitle } = usePageWrapperData();
  const {
    title,
    digitalExhibitSettings,
    digitalExhibitHero,
    digitalExhibitContent: {
      intro,
      modules,
      pageEnder,
    } = {},
    pageData: {
      ogTitle,
      ogDescription,
      featuredImage,
      pageCategory,
    } = {},
  } = data?.rhDigitalExhibit || {};
  const [category] = pageCategory || [];
  const seoTitle = getPageTitle(siteTitle, ogTitle || title);

  // Sound popup background: editors pick which accent fills it (Sound Popup
  // Color setting). The popup wrapper renders outside the theme wrapper, so
  // the accent hex is resolved here instead of via the --dx-accent-* custom
  // properties. Falls back to the site green when the accent has no color set.
  const { accent1, accent2, accent3, soundPopupColor, soundPopupOverride, navFontColor, fontTheme, textCase } = digitalExhibitSettings || {};
  const dxFont = resolveDxFont(fontTheme);
  const dxCase = resolveDxCase(textCase, dxFont);
  const soundPopupAccents: Record<string, string | null | undefined> = {
    accent_1: accent1,
    accent_2: accent2,
    accent_3: accent3,
  };
  const popupColor = soundPopupAccents[selectValue(soundPopupColor)] || "var(--green)";

  useTrackRouteChange(category?.slug);

  // Nav rollovers and default highlighted text take the exhibit's first accent
  useEffect(() => {
    if (digitalExhibitSettings?.accent1) setHighlightColor(digitalExhibitSettings.accent1);
  }, [digitalExhibitSettings?.accent1]);

  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  // Smooth scroll — Lenis, same wiring as InducteesLandingWrapper (GSAP's
  // ticker drives Lenis so ScrollTrigger stays in sync). The prototype runs
  // Lenis too (lerp 0.18, smoothWheel; touch stays native). Skipped under
  // reduced motion.
  const lenisRef = useRef<LenisRef>(null);
  const reduceMotion = getReduceMotion();
  useEffect(() => {
    if (getReduceMotion()) return;
    const update = (time: number) => {
      lenisRef.current?.lenis?.raf(time * 1000);
      ScrollTrigger.update();
    };
    gsap.ticker.add(update);
    gsap.ticker.lagSmoothing(0);
    return () => gsap.ticker.remove(update);
  }, []);

  // Deep links to a specific module resolve to its auto-assigned `dx-<slug>`
  // anchor (e.g. dx-carousel) via either entry point:
  //  - the share URL's `?module=<slug>` query (also drives server-rendered OG —
  //    see /api/dx-og + middleware)
  //  - a `#<slug>` hash from an internal-link CTA elsewhere on the site (primary
  //    /secondary hero, Banner). Digital exhibits have no sticky nav, so the
  //    site-wide StickyNav scroll never runs here — we resolve the hash instead.
  //    Editors enter just the slug (e.g. `carousel`); the `dx-` prefix is added
  //    here, so the section ID matches the share slug.
  // NOTE: when new shareable/linkable modules ship (quiz, music explorer, deep
  // dive — not built yet), give each a stable `dx-<slug>` id on its section and
  // it becomes deep-linkable here automatically. See DigitalExhibitContentMap.
  const router = useRouter();
  useEffect(() => {
    const scrollToTarget = () => {
      // Target slug from the share URL (?module=) or a section hash (#slug) on a
      // CTA link. Read the hash from window.location directly (not router.asPath,
      // whose post-hydration update would re-fire and re-scroll).
      const fromQuery = typeof router.query?.module === "string" ? router.query.module : null;
      const fromHash = typeof window !== "undefined" ? window.location.hash.slice(1) || null : null;
      // The Deep Dive is now a plain deep-linkable section like every other
      // module: `#deep-dive` resolves to its `dx-deep-dive` anchor and scrolls
      // here. Its drawer opens only via the on-page CTA, so there is no longer a
      // special drawer hash to exclude.
      const target = fromQuery || fromHash;
      if (!target) return;
      // Scroll, then CORRECT after each smooth scroll finishes. On client-side
      // nav the hero + module ScrollTriggers insert/grow pin-spacers over ~1s,
      // so the target keeps moving even after the scroll starts and a single
      // computed scroll lands short. Re-scroll (briefly) until the element rests
      // at the top. On a full load the layout is already stable, so the first
      // scroll lands and no correction fires.
      const scrollToEl = (el: HTMLElement, attempt: number) => {
        const reduce = getReduceMotion();
        const lenis = lenisRef.current?.lenis;
        const correct = () => {
          if (attempt >= 4) return;
          if (Math.abs(el.getBoundingClientRect().top) > 6) scrollToEl(el, attempt + 1);
        };
        if (lenis && !reduce) {
          // Lenis caches scroll dimensions; after a client-side nav they're
          // STALE (measured on the previous, often shorter page), so scrollTo(el)
          // targets the wrong position and lands short. Re-measure first so the
          // FIRST scroll goes straight to the module in one motion instead of
          // scrolling to the wrong spot and then correcting. The correction
          // below stays as a backstop for any genuine post-scroll layout shift.
          lenis.resize();
          lenis.scrollTo(el, { duration: attempt === 0 ? 1.2 : 0.4, onComplete: correct });
        } else {
          el.scrollIntoView({ behavior: reduce ? "auto" : "smooth" });
          window.setTimeout(correct, reduce ? 0 : 500);
        }
      };
      // The module renders a beat after the route resolves on client-side nav,
      // and its offset can keep shifting as pins initialize — wait until it
      // exists and its offset holds steady, then scroll. Capped as a backstop.
      let frames = 0;
      let lastTop = NaN;
      let stable = 0;
      const waitThenScroll = () => {
        frames += 1;
        const el = document.getElementById(`dx-${target}`);
        if (el) {
          const top = el.offsetTop;
          if (top === lastTop) stable += 1;
          else {
            stable = 0;
            lastTop = top;
          }
          if (stable >= 6 || frames >= 240) {
            scrollToEl(el, 0);
            return;
          }
        }
        requestAnimationFrame(waitThenScroll);
      };
      requestAnimationFrame(waitThenScroll);
    };

    // Fresh arrival (full load, or client-nav from another template that mounts
    // this page): scroll once content is ready.
    if (!loading) scrollToTarget();
    // Client-nav between exhibits reuses this template without remounting, so
    // the effect above wouldn't re-run — listen for Next's route/hash changes
    // and re-resolve the deep link. (Next's own scroll-to-top for the unmatched
    // dx-prefixed hash is a harmless no-op on a fresh nav and runs before this,
    // so no Link-level scroll suppression is needed.)
    router.events.on("routeChangeComplete", scrollToTarget);
    router.events.on("hashChangeComplete", scrollToTarget);
    return () => {
      router.events.off("routeChangeComplete", scrollToTarget);
      router.events.off("hashChangeComplete", scrollToTarget);
    };
  }, [router.query?.module, router.events, loading]);

  if (loading) return;
  return (
    <>
      <SEO title={seoTitle} description={ogDescription} image={featuredImage} />
      {!reduceMotion && (
        <ReactLenis
          root
          options={{ autoRaf: false, allowNestedScroll: true, lerp: 0.18, smoothWheel: true, wheelMultiplier: 1 }}
          ref={lenisRef}
        />
      )}
      <DigitalExhibitTheme
        fontId={dxFont.id}
        dxCase={dxCase}
        accent1={accent1}
        accent2={accent2}
        accent3={accent3}
        navFontColor={navFontColor}
      >
        <DigitalExhibitHeroIntro hero={digitalExhibitHero} {...intro} />
        <DigitalExhibitContentMap modules={modules} fontTheme={dxFont.id} textCase={dxCase} />
        <DigitalExhibitPageEnder {...pageEnder} />
      </DigitalExhibitTheme>
      {/* Outside the theme wrapper: its isolation traps the popup's z-index in
          a z-auto stacking context that the footer paints over. Background is
          the accent chosen in the Sound Popup Color setting. */}
      <div
        className="dx-sound-popup-wrap"
        style={{ "--highlight-color": popupColor } as React.CSSProperties}
      >
        <SoundPopup headlineOverride={soundPopupOverride} />
      </div>
    </>
  );
};

DigitalExhibit.query = gql`
  ${DigitalExhibitDetailFragment}
  query DigitalExhibit($databaseId: ID!, $asPreview: Boolean = false, $language: String!) {
    rhDigitalExhibit(id: $databaseId, asPreview: $asPreview, language: $language) {
      ...DigitalExhibitDetailFragment
    }
  }
`;

DigitalExhibit.variables = getBasicQueryVars;

export default DigitalExhibit;
